import styled from 'styled-components';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import InfoChip from 'components/shared/InfoChip';
import StandardModal from 'components/shared/StandardModal';
import { NetworkStatus, PAYER_TYPES } from 'models/GroupPayerNetworkStatus';
import { GroupPayerNetworkStatusOnAction } from 'models/WorkflowAction';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import CheckIcon from 'svg/CheckIcon';
import XIcon from 'svg/XIcon';

export function NetworkStatusLink({ status, onView }: { status: GroupPayerNetworkStatusOnAction; onView: () => void }) {
  if (!status.configured) return <span>No Network Status Configured</span>;

  return (
    <LinkButton type='button' onClick={onView} data-testid='view-network-status'>
      View Network Status
    </LinkButton>
  );
}

export function NetworkStatusModal({
  facilityName,
  status,
  onClose,
}: {
  facilityName: string;
  status: GroupPayerNetworkStatusOnAction;
  onClose: () => void;
}) {
  return (
    <StandardModal title={`${facilityName} – Network Status`} onCancel={onClose}>
      <Content>
        {status.stediPayerName && <PayerLine>Primary Payer: {status.stediPayerName}</PayerLine>}
        <div>
          <SectionLabel>Network Status</SectionLabel>
          <StatusCard>
            {PAYER_TYPES.map(({ field, label }) => (
              <StatusRow key={field} data-testid={`network-status-${field}`}>
                <PayerType>{label}</PayerType>
                <StatusChip value={status[field]} />
              </StatusRow>
            ))}
          </StatusCard>
        </div>
      </Content>
      <Buttons>
        <Button variant='primary' size='small' onClick={onClose}>
          Close
        </Button>
      </Buttons>
    </StandardModal>
  );
}

function StatusChip({ value }: { value: NetworkStatus }) {
  if (value === 'in') {
    return (
      <Chip $size='small' $backgroundColor={colors.green10} $color={colors.green100}>
        <CheckIcon width={8} height={6} fill={colors.green100} />
        In Network
      </Chip>
    );
  }
  if (value === 'out') {
    return (
      <Chip $size='small' $backgroundColor={colors.red10} $color={colors.red100}>
        <XIcon width={6} height={6} fill={colors.red100} />
        Out of Network
      </Chip>
    );
  }
  return <Chip $size='small'>Not Set</Chip>;
}

const LinkButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${colors.blue100};
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
`;

// 36px below the header: StandardModal's content already pads 24px.
const PayerLine = styled(BodySmallBold)`
  margin-top: 12px;
  text-align: center;
`;

const SectionLabel = styled(BodySmall)`
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  color: ${colors.black75};
`;

const StatusCard = styled.div`
  border: 1px solid ${colors.black10};
  border-radius: 4px;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 16px;

  & + & {
    border-top: 1px solid ${colors.black10};
  }
`;

const PayerType = styled(BodySmallBold)`
  font-size: 12px;
`;

const Chip = styled(InfoChip)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 10px;
  line-height: 12px;
`;

const Buttons = styled(ButtonGroup)`
  justify-content: flex-end;
  margin-top: 36px;
`;
